import React, { useEffect, useState } from 'react'

import { setRunsheetShiftTableLoading } from '@/entities/gridTables/slices/runsheetShiftTablesSlice'
import { moveBreak } from '@/entities/gridTables/thunks/moveBreak'
import { processDropOnShift, ProcessDropOnShiftPayload } from '@/entities/gridTables/thunks/processDropOnShift'
import { updateRunsheetShiftTableState } from '@/entities/gridTables/thunks/updateRunsheetShiftTableState'
import { fetchRunsheets } from '@/entities/schedules'
import { upsertOneWidgetWindow } from '@/entities/widgetWindows'
import { GanttChart } from '@/shared/components/GanttChart/GanttChart'
import { TimelineStop } from '@/shared/components/GanttChart/models/GanttRow'
import { WidgetTypeWithSuffix } from '@/shared/components/Mosaic'
import { TOOL_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch } from '@/shared/store'
import { faChartBar } from '@fortawesome/pro-regular-svg-icons'

import { useRunsheetGanttChartData } from './libs/useRunsheetGanttChartData'
import { useOperationsGanttContext } from './OperationsGanttChartContext'

export type RunsheetGanttChartProps = {
  id: string
  widgetId: WidgetTypeWithSuffix // Widget Window id
}

export const RunsheetGanttChart: React.FC<RunsheetGanttChartProps> = ({ widgetId }) => {
  const [loading, setLoading] = useState(false)
  
  const { setWidgetId } = useOperationsGanttContext()
  const { rows, minStartTime, maxEndTime } = useRunsheetGanttChartData({ widgetId })
  const dispatch = useAppDispatch()

  // Update widget window context and global store to render widow title, uiColor, etc.
  useEffect(() => {
    if (widgetId) {
      setWidgetId(widgetId as `GanttChart-${string}`)
      dispatch(upsertOneWidgetWindow({
        id: widgetId,
        shiftIds: [],
        isActive: true,
        widgetType: TOOL_WIDGETS.GanttChart,
        navigations: [
          {
            title: 'Gantt Chart',
            icon: faChartBar,
          },
        ],
      }))
    }
  }, [widgetId, dispatch, setWidgetId])

  const onStopDrop = async (sourceStop: TimelineStop, targetStop: TimelineStop) => {
    if (loading) return

    const sourceShiftId = sourceStop.shiftId
    const targetShiftId = targetStop.shiftId
    const isSourceBreak = sourceStop.displayStopType === 'Break'
    const sourceStopIds = [sourceStop.id]
    const targetStopId = targetStop.id

    const payload: ProcessDropOnShiftPayload = {
      orderIds: null,
      sourceShiftId,
      shiftId: targetShiftId,
      stopId: targetStopId,
      stopIds: sourceStopIds,
    }

    // Set loading state for Gantt and Shift tables
    setLoading(true)
    dispatch(setRunsheetShiftTableLoading({ id: targetStop.shiftId, isLoading: true }))

    if (payload.sourceShiftId) {
      dispatch(setRunsheetShiftTableLoading({ id: payload.sourceShiftId, isLoading: true }))
    }

    // Process drop on shift
    if (isSourceBreak) {
      await dispatch(moveBreak({ stopId: sourceStop.id, targetStopId: targetStop.id }))
      await dispatch(fetchRunsheets({ runsheetIds: [sourceStop.scheduleId] }))
    }
    else {
      await dispatch(processDropOnShift(payload))
      await dispatch(fetchRunsheets({ runsheetIds: [targetStop.scheduleId, sourceStop.scheduleId] }))
    }

    await Promise.allSettled([
      dispatch(updateRunsheetShiftTableState({ shiftId: targetStop.shiftId })),
      payload.sourceShiftId ? dispatch(updateRunsheetShiftTableState({ shiftId: payload.sourceShiftId })) : Promise.resolve(),
    ])

    // Reset loading state
    setLoading(false)
    dispatch(setRunsheetShiftTableLoading({ id: targetStop.shiftId, isLoading: false }))

    if (payload.sourceShiftId) {
      dispatch(setRunsheetShiftTableLoading({ id: payload.sourceShiftId, isLoading: false }))
    }
  }
  
  return (
    <GanttChart
      rows={rows}
      startHour={Math.max(0, minStartTime.hour() - 1)}
      endHour={Math.min(24, maxEndTime.hour() + 1)}
      showTimeline
      onStopDrop={onStopDrop}
      loading={loading}
    />
  )
}
